/**
 * Content Security Policy (CSP) Configuration
 *
 * Defines allowed sources for loading resources to prevent XSS attacks and data injection.
 * Each directive controls what resources can be loaded from which origins.
 *
 * Reference: https://developer.mozilla.org/en-US/docs/Web/HTTP/CSP
 */
const directives: Record<string, string[]> = {
  /**
   * default-src: Fallback for other fetch directives
   * Applies when specific directives (script-src, style-src, etc.) are not defined
   */
  'default-src': [
    "'self'", // Same origin
    '*.vanguard.com:*', // Vanguard domains (all ports)
    '*.vgcontent.info:*', // Vanguard content CDN
    '*.kampyle.com', // Medallia feedback widget
  ],

  /**
   * script-src: Controls which scripts can be executed
   * WARNING: 'unsafe-eval' and 'unsafe-inline' reduce security - required for legacy dependencies
   */
  'script-src': [
    "'self'", // Same origin scripts
    '*.vanguard.com:*', // Vanguard domains
    '*.vgcontent.info:*', // Vanguard content CDN

    // Adobe Analytics & Marketing
    '*.demdex.net', // Adobe Audience Manager
    'fls.doubleclick.net', // DoubleClick Floodlight
    '*.fls.doubleclick.net', // DoubleClick Floodlight wildcard
    'vanguard.d2.sc.omtrdc.net', // Adobe Analytics data collection
    "'unsafe-eval'", // Required for Adobe App Measurement library
    '*.omniture.com', // Adobe Analytics (legacy)
    'activitymap.adobe.com', // Adobe Activity Map
    '*.tt.omtrdc.net', // Adobe Target
    '*.omtrdc.net', // Adobe Marketing Cloud

    // Dynamic content & experimentation
    '*.vgdynamic.info', // Vanguard dynamic content

    // User feedback & surveys
    '*.kampyle.com', // Medallia feedback widget
    '*.medallia.com', // Medallia surveys

    // Privacy & consent management
    '*.onetrust.com', // OneTrust cookie consent platform
    'cdn.cookielaw.org', // OneTrust CDN

    // Advertising & tracking
    "'unsafe-inline'", // Required for event handlers in legacy VGN header
    '*.adsymptotic.com', // Ad tech provider
    '*.linkedin.com', // LinkedIn Insights
    '*.adsrvr.org', // The Trade Desk
    '*.analytics.yahoo.com', // Yahoo Analytics
    '*.amazon-adsystem.com', // Amazon advertising
    '*.doubleclick.net', // Google DoubleClick

    // Google services
    '*.googleapis.com', // Google APIs
    '*.gstatic.com', // Google static content
    'www.google.com', // Google services

    // Customer service
    '*.mypurecloud.com', // Genesys Cloud customer service
    'wss://*.mypurecloud.com', // Genesys Cloud WebSocket
  ],
  /**
   * style-src: Controls which stylesheets can be applied
   * WARNING: 'unsafe-inline' reduces security - required for legacy VGN header and FASET
   */
  'style-src': [
    "'self'", // Same origin stylesheets
    '*.vanguard.com:*', // Vanguard domains
    '*.vgcontent.info:*', // Vanguard content CDN
    "'unsafe-inline'", // Required by legacy VGN header and experimentation app

    // Third-party services that inject styles
    '*.kampyle.com', // Medallia feedback widget
    '*.medallia.com', // Medallia surveys
    '*.tt.omtrdc.net', // Adobe Target
    '*.omtrdc.net', // Adobe Marketing Cloud
    '*.onetrust.com', // OneTrust cookie consent
    'cdn.cookielaw.org', // OneTrust CDN

    // Advertising platforms
    '*.adsymptotic.com', // Ad tech provider
    '*.linkedin.com', // LinkedIn Insights
    '*.adsrvr.org', // The Trade Desk
    '*.analytics.yahoo.com', // Yahoo Analytics
    '*.amazon-adsystem.com', // Amazon advertising
    '*.doubleclick.net', // Google DoubleClick

    // Google services
    '*.googleapis.com', // Google APIs (fonts, etc.)
    '*.gstatic.com', // Google static content
    'www.google.com', // Google services

    // Customer service
    '*.mypurecloud.com', // Genesys Cloud
    'wss://*.mypurecloud.com', // Genesys Cloud WebSocket
  ],
  /**
   * connect-src: Controls which URLs can be loaded using fetch, XMLHttpRequest, WebSocket, etc.
   * Used for API calls, analytics beacons, and real-time connections
   */
  'connect-src': [
    "'self'", // Same origin API calls
    '*.vanguard.com:*', // Vanguard APIs
    '*.vgcontent.info:*', // Vanguard content CDN

    // Adobe Analytics & Marketing
    '*.demdex.net', // Adobe Audience Manager
    'fls.doubleclick.net', // DoubleClick Floodlight
    '*.fls.doubleclick.net', // DoubleClick Floodlight wildcard
    'vanguard.d2.sc.omtrdc.net', // Adobe Analytics data collection
    '*.tt.omtrdc.net', // Adobe Target
    '*.doubleclick.net', // Google DoubleClick

    // Application monitoring
    '*.eum-appdynamics.com', // AppDynamics monitoring

    // User feedback & surveys
    '*.kampyle.com', // Medallia feedback widget
    '*.medallia.com', // Medallia surveys

    // Privacy & consent management
    '*.onetrust.com', // OneTrust cookie consent
    'privacyportal-de.onetrust.com', // OneTrust privacy portal (Germany)
    'geolocation.onetrust.com', // OneTrust geolocation service
    'cdn.cookielaw.org', // OneTrust CDN

    // Advertising & tracking
    '*.adsymptotic.com', // Ad tech provider
    '*.linkedin.com', // LinkedIn Insights
    '*.adsrvr.org', // The Trade Desk
    '*.analytics.yahoo.com', // Yahoo Analytics
    '*.amazon-adsystem.com', // Amazon advertising

    // Google services
    '*.googleapis.com', // Google APIs
    '*.gstatic.com', // Google static content
    'www.google.com', // Google services

    // Customer service
    '*.mypurecloud.com', // Genesys Cloud customer service
    'wss://*.mypurecloud.com', // Genesys Cloud WebSocket connection
  ],
  /**
   * img-src: Controls which sources can load images
   * Includes tracking pixels, analytics beacons, and content images
   */
  'img-src': [
    "'self'", // Same origin images
    '*.vanguard.com:*', // Vanguard domains
    '*.vgcontent.info:*', // Vanguard content CDN
    'data:', // Data URIs for inline images (e.g., base64 encoded)

    // Adobe Analytics & Marketing
    '*.demdex.net', // Adobe Audience Manager
    'fls.doubleclick.net', // DoubleClick Floodlight tracking pixels
    '*.fls.doubleclick.net', // DoubleClick Floodlight wildcard
    'vanguard.d2.sc.omtrdc.net', // Adobe Analytics beacons
    '*.omtrdc.net', // Adobe Marketing Cloud
    '*.doubleclick.net', // Google DoubleClick

    // Application monitoring
    '*.eum-appdynamics.com', // AppDynamics monitoring

    // Dynamic content
    '*.vgdynamic.info', // Vanguard dynamic content
    't.myvisualiq.net', // Visual IQ marketing analytics

    // User feedback & surveys
    '*.kampyle.com', // Medallia feedback widget
    '*.medallia.com', // Medallia surveys

    // Privacy & consent management
    '*.onetrust.com', // OneTrust cookie consent
    'cdn.cookielaw.org', // OneTrust CDN

    // Advertising & tracking pixels
    '*.facebook.com', // Facebook Pixel
    '*.amazon-adsystem.com', // Amazon advertising
    '*.analytics.yahoo.com', // Yahoo Analytics
    'insight.adsrvr.org', // The Trade Desk pixel
    '*.adsrvr.org', // The Trade Desk
    '*.ads.linkedin.com', // LinkedIn Insights tag
    '*.linkedin.com', // LinkedIn Insights
    'p.adsymptotic.com', // Ad tech provider pixel
    '*.adsymptotic.com', // Ad tech provider

    // Google services
    '*.googleapis.com', // Google APIs
    '*.gstatic.com', // Google static content
    'www.google.com', // Google services

    // Customer service
    '*.mypurecloud.com', // Genesys Cloud
    'wss://*.mypurecloud.com', // Genesys Cloud WebSocket
  ],
  /**
   * frame-src: Controls which sources can be embedded as iframes
   * Used for embedded content, widgets, and third-party integrations
   */
  'frame-src': [
    "'self'", // Same origin iframes
    '*.vanguard.com:*', // Vanguard domains
    '*.vgcontent.info:*', // Vanguard content CDN

    // Adobe Analytics & Marketing
    '*.demdex.net', // Adobe Audience Manager
    'fls.doubleclick.net', // DoubleClick Floodlight (required for Floodlight tagging)
    '*.fls.doubleclick.net', // DoubleClick Floodlight wildcard
    'vanguard.d2.sc.omtrdc.net', // Adobe Analytics
    '*.omniture.com', // Adobe Analytics (legacy)
    'activitymap.adobe.com', // Adobe Activity Map
    '*.omtrdc.net', // Adobe Marketing Cloud
    '*.doubleclick.net', // Google DoubleClick

    // User feedback & surveys
    '*.kampyle.com', // Medallia feedback widget
    '*.medallia.com', // Medallia surveys

    // Privacy & consent management
    '*.onetrust.com', // OneTrust cookie consent
    'cdn.cookielaw.org', // OneTrust CDN

    // Advertising & tracking
    'insight.adsrvr.org', // The Trade Desk
    '*.adsrvr.org', // The Trade Desk
    '*.adsymptotic.com', // Ad tech provider
    '*.linkedin.com', // LinkedIn Insights
    '*.analytics.yahoo.com', // Yahoo Analytics
    '*.amazon-adsystem.com', // Amazon advertising

    // Google services
    '*.googleapis.com', // Google APIs
    '*.gstatic.com', // Google static content
    'www.google.com', // Google services

    // Customer service
    '*.mypurecloud.com', // Genesys Cloud customer service widget
    'wss://*.mypurecloud.com', // Genesys Cloud WebSocket
  ],

  /**
   * object-src: Controls which sources can be used for <object>, <embed>, and <applet> elements
   * Restricted to Vanguard domains only for security
   */
  'object-src': [
    "'self'", // Same origin plugins
    '*.vanguard.com:*', // Vanguard domains
    '*.vgcontent.info:*', // Vanguard content CDN
  ],
};

const script_policies: string = directives['script-src'].join(' ');
const img_policies: string = directives['img-src'].join(' ');
const style_policies: string = directives['style-src'].join(' ');
const connect_policies: string = directives['connect-src'].join(' ');

const frames_policies: string = directives['frame-src'].join(' ');

const object_policies: string = directives['object-src'].join(' ');

export interface CSP {
  readonly 'img-src': string;
  readonly 'script-src': string;
  readonly 'style-src': string;
  readonly 'connect-src': string;
  readonly 'frame-src': string;
  readonly 'object-src': string;
}

export const csp: CSP = {
  'img-src': img_policies,
  'script-src': script_policies,
  'style-src': style_policies,
  'connect-src': connect_policies,
  'frame-src': frames_policies,
  'object-src': object_policies,
};
